import React, { useMemo, useState } from 'react';
import API from './api';

const CODE = /^[A-HJ-NP-Z2-9]{4}-?[A-HJ-NP-Z2-9]{4}$/;

function displayCode(value) {
  const compact = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return compact.length > 4 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact;
}

export default function TvPairing() {
  const initial = useMemo(() => displayCode(new URLSearchParams(window.location.search).get('code')), []);
  const [code, setCode] = useState(initial);
  const [state, setState] = useState('entry');
  const [message, setMessage] = useState('');

  const submit = async (decision) => {
    const normalized = displayCode(code);
    if (!CODE.test(normalized)) {
      setMessage('Enter the 8-character code shown on your TV.');
      return;
    }
    setState('working');
    setMessage('');
    try {
      await API.post('/v1/desktop/tv/approve', { user_code: normalized, decision });
      setState(decision === 'approve' ? 'approved' : 'denied');
    } catch (error) {
      setState('entry');
      const reason = error?.response?.data?.error;
      setMessage(reason === 'code_not_available'
        ? 'That code has expired or has already been used. Start sign-in again on your TV.'
        : 'We could not confirm that code. Check it and try again.');
    }
  };

  return (
    <main style={styles.page}>
      <section style={styles.card} aria-labelledby="tv-pairing-title">
        <div style={styles.brand}>VISINVOKE</div>
        {state === 'approved' ? (
          <>
            <h1 id="tv-pairing-title" style={styles.title}>TV connected</h1>
            <p style={styles.copy}>Return to your TV. VISINVOKE will finish signing in automatically.</p>
          </>
        ) : state === 'denied' ? (
          <>
            <h1 id="tv-pairing-title" style={styles.title}>Connection cancelled</h1>
            <p style={styles.copy}>No account access was given to the TV.</p>
          </>
        ) : (
          <>
            <h1 id="tv-pairing-title" style={styles.title}>Connect your TV</h1>
            <p style={styles.copy}>Enter the code shown in the VISINVOKE app on your TV.</p>
            <label htmlFor="tv-code" style={styles.label}>TV code</label>
            <input id="tv-code" autoComplete="one-time-code" inputMode="text" autoFocus
              value={code} onChange={(event) => setCode(displayCode(event.target.value))}
              placeholder="ABCD-2345" maxLength={9} style={styles.input} />
            {message ? <p role="alert" style={styles.error}>{message}</p> : null}
            <button type="button" disabled={state === 'working'} onClick={() => submit('approve')} style={styles.primary}>
              {state === 'working' ? 'Connecting…' : 'Connect TV'}
            </button>
            <button type="button" disabled={state === 'working'} onClick={() => submit('deny')} style={styles.secondary}>
              Cancel
            </button>
            <p style={styles.safety}>Only approve a code that is currently visible on your TV.</p>
          </>
        )}
      </section>
    </main>
  );
}

const styles = {
  page: { minHeight: '100vh', boxSizing: 'border-box', padding: 24, display: 'grid', placeItems: 'center', background: 'radial-gradient(circle at top, #1d2c50, #080d18 60%)', color: '#f7f9ff', fontFamily: 'Inter, system-ui, sans-serif' },
  card: { width: '100%', maxWidth: 520, boxSizing: 'border-box', padding: '38px 34px', borderRadius: 18, background: '#101a2d', border: '1px solid #33496f', boxShadow: '0 24px 70px rgba(0,0,0,.45)' },
  brand: { color: '#a985ff', fontSize: 15, fontWeight: 900, letterSpacing: '.12em' },
  title: { margin: '12px 0 10px', fontSize: 34, lineHeight: 1.15 },
  copy: { margin: '0 0 28px', color: '#c8d2e5', fontSize: 18, lineHeight: 1.55 },
  label: { display: 'block', marginBottom: 8, fontWeight: 800, fontSize: 16 },
  input: { width: '100%', boxSizing: 'border-box', border: '2px solid #6177a4', borderRadius: 12, padding: '16px 18px', background: '#081222', color: '#fff', fontSize: 30, lineHeight: 1, fontWeight: 900, letterSpacing: '.14em', textAlign: 'center', textTransform: 'uppercase' },
  error: { color: '#ffb7b7', fontSize: 15, lineHeight: 1.4, margin: '12px 0 0' },
  primary: { width: '100%', marginTop: 22, padding: '15px 18px', border: 0, borderRadius: 12, color: '#fff', background: 'linear-gradient(135deg,#7045e8,#9b5cff)', fontSize: 18, fontWeight: 900, cursor: 'pointer' },
  secondary: { width: '100%', marginTop: 10, padding: '13px 18px', border: '1px solid #52668d', borderRadius: 12, color: '#dce5f6', background: 'transparent', fontSize: 16, fontWeight: 800, cursor: 'pointer' },
  safety: { margin: '20px 0 0', color: '#93a3bf', fontSize: 14, lineHeight: 1.5 },
};
